import { get as apiGet, put as apiPut } from './apiService';
import type { RecipientActivity, ActivityCountResponse } from '../types/activity';

export async function getActivities(
  profileId: string,
  status: string,
  fetchSize: number,
  lastActivityId?: string,
): Promise<RecipientActivity[]> {
  let url = `/activity/recipient/${profileId}?status=${status}&fetchSize=${fetchSize}`;
  if (lastActivityId) url += `&lastActivityId=${lastActivityId}`;
  return apiGet<RecipientActivity[]>(url);
}

export async function getActivityCount(
  profileId: string,
  status: string,
): Promise<ActivityCountResponse> {
  return apiGet<ActivityCountResponse>(`/activity/recipient/${profileId}/count?status=${status}`);
}

export async function markActivitiesRead(
  profileId: string,
  activityIds: string[],
): Promise<void> {
  await apiPut<void>(`/activity/recipient/${profileId}?status=read`, activityIds);
}

/**
 * Flip activities back to unread — the undo of a mark-read. Same endpoint,
 * `status=new`, which clears `readAt` server-side.
 */
export async function markActivitiesUnread(
  profileId: string,
  activityIds: string[],
): Promise<void> {
  await apiPut<void>(`/activity/recipient/${profileId}?status=new`, activityIds);
}

/**
 * Every unread activity id for a profile, walked page by page.
 *
 * `markActivitiesRead` takes an explicit id list and there's no server-side
 * "mark everything" route, so Mark-all-read has to enumerate first. Paging is
 * what makes the resulting undo honest: the toast can put back exactly the set
 * it cleared. Bounded by MAX_PAGES so a runaway cursor can't spin forever.
 */
const ALL_UNREAD_PAGE_SIZE = 100;
const MAX_PAGES = 20;

export async function getAllUnreadActivityIds(profileId: string): Promise<string[]> {
  const ids: string[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page++) {
    const batch = await getActivities(profileId, 'new', ALL_UNREAD_PAGE_SIZE, cursor);
    ids.push(...batch.map((a) => a.id));
    if (batch.length < ALL_UNREAD_PAGE_SIZE) break;
    cursor = batch[batch.length - 1].id;
  }
  return ids;
}
